import { useState, useEffect } from 'react';
import { Link } from 'wouter';
import { Github, Twitter, Linkedin, Heart, Coffee } from 'lucide-react';

// API URL from environment variable, fallback to relative path (Vite proxies to localhost:3000)
const API_URL = import.meta.env.VITE_API_URL || '';

export function Footer() {
  const [serverVersion, setServerVersion] = useState<string | null>(null);

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const response = await fetch(`${API_URL}/api`);
        if (response.ok) {
          const data = await response.json();
          setServerVersion(data.version);
        }
      } catch (error) {
        // Silently fail - will show fallback version
        console.debug('Could not fetch server version:', error);
      }
    };

    fetchVersion();
  }, []);

  return (
    <footer className="bg-slate-900 text-slate-300">
      <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
          {/* Product */}
          <div>
            <h3 className="text-white font-semibold mb-4">Product</h3>
            <ul className="space-y-3">
              <li>
                <Link href="/product">
                  <a className="hover:text-white transition-colors">Features</a>
                </Link>
              </li>
              <li>
                <Link href="/pricing">
                  <a className="hover:text-white transition-colors">Pricing</a>
                </Link>
              </li>
              <li>
                <a
                  href="https://docs.dakkio.io"
                  className="hover:text-white transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Documentation
                </a>
              </li>
              <li>
                <a
                  href="https://docs.dakkio.io/api"
                  className="hover:text-white transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  API Reference
                </a>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-white font-semibold mb-4">Company</h3>
            <ul className="space-y-3">
              <li>
                <Link href="/blog">
                  <a className="hover:text-white transition-colors">Blog</a>
                </Link>
              </li>
              <li>
                <a
                  href="https://github.com/dakkio"
                  className="hover:text-white transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  GitHub
                </a>
              </li>
              <li>
                <a
                  href="mailto:hello@dakkio.io"
                  className="hover:text-white transition-colors"
                >
                  Contact
                </a>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-white font-semibold mb-4">Resources</h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="https://docs.dakkio.io/guides"
                  className="hover:text-white transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Guides
                </a>
              </li>
              <li>
                <a
                  href="https://docs.dakkio.io/examples"
                  className="hover:text-white transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Examples
                </a>
              </li>
              <li>
                <a
                  href="https://status.dakkio.io"
                  className="hover:text-white transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Status
                </a>
              </li>
              <li>
                <a
                  href="https://discord.gg/dakkio"
                  className="hover:text-white transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Community
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-white font-semibold mb-4">Legal</h3>
            <ul className="space-y-3">
              <li>
                <Link href="/privacy">
                  <a className="hover:text-white transition-colors">Privacy Policy</a>
                </Link>
              </li>
              <li>
                <Link href="/terms">
                  <a className="hover:text-white transition-colors">Terms of Service</a>
                </Link>
              </li>
              <li>
                <Link href="/cookies">
                  <a className="hover:text-white transition-colors">Cookie Policy</a>
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Kio Family & Partners Section */}
        <div className="pt-8 border-t border-slate-800 pb-6">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Used By */}
            <div>
              <h4 className="text-white font-semibold mb-3 text-sm">Trusted By</h4>
              <div className="flex flex-wrap gap-3 text-sm">
                <a
                  href="https://remotesystems.ro"
                  className="text-slate-400 hover:text-white transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  RemoteSystems.ro
                </a>
                <span className="text-slate-600">•</span>
                <a
                  href="https://beepulse.io"
                  className="text-slate-400 hover:text-white transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  BeePulse.io
                </a>
                <span className="text-slate-600">•</span>
                <a
                  href="https://stupio.ro"
                  className="text-slate-400 hover:text-white transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Stupio.ro
                </a>
              </div>
            </div>

            {/* Kio Family */}
            <div>
              <h4 className="text-white font-semibold mb-3 text-sm">Part of the Kio Stack</h4>
              <div className="flex flex-wrap gap-3 text-sm">
                <a
                  href="https://dokkio.io"
                  className="text-slate-400 hover:text-white transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  dokkio
                </a>
                <span className="text-slate-600">•</span>
                <span className="text-slate-500">
                  trakkio <span className="text-xs">(WIP)</span>
                </span>
                <span className="text-slate-600">•</span>
                <span className="text-slate-500">
                  lokkio <span className="text-xs">(WIP)</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="pt-6 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center space-x-2 mb-4 md:mb-0">
            <div className="w-6 h-6 bg-gradient-to-br from-primary-600 to-secondary-500 rounded"></div>
            <span className="text-white font-bold">dakkio</span>
          </div>

          <div className="text-sm text-slate-400 mb-4 md:mb-0 text-center">
            <div className="mb-1 flex items-center justify-center gap-1 flex-wrap">
              <span>Made with</span>
              <Heart className="w-4 h-4 text-red-500 fill-red-500" />
              <span>and a lot of</span>
              <Coffee className="w-4 h-4 text-amber-400" />
              <span>by</span>
            </div>
            <div className="mb-1">
              <a
                href="https://malexsoftware.com"
                className="text-slate-300 hover:text-white transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                MalexSoftware
              </a>
            </div>
            <div>© 2026 dakkio.io. All rights reserved.</div>
            <div className="text-slate-500 text-xs mt-1">v{serverVersion || __APP_VERSION__}</div>
          </div>

          <div className="flex items-center space-x-4">
            <a
              href="https://twitter.com/dakkio"
              className="text-slate-400 hover:text-white transition-colors"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Twitter"
            >
              <Twitter className="w-5 h-5" />
            </a>
            <a
              href="https://github.com/dakkio"
              className="text-slate-400 hover:text-white transition-colors"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub"
            >
              <Github className="w-5 h-5" />
            </a>
            <a
              href="https://linkedin.com/company/dakkio"
              className="text-slate-400 hover:text-white transition-colors"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
            >
              <Linkedin className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
